/**
 * State shared by the three columns of the simple workspace: which volumes
 * the centre draws, which file the right column previews, and the file list
 * both read from (the opened subject's inputs plus every pipeline output).
 */

import * as React from "react";
import { create } from "zustand";

import { getAssetUrl, listOutputs } from "@/ipc/api";
import type { DetectedFile } from "@/ipc/contract";

export interface SimpleFile {
  /** Path `getAssetUrl` resolves: an output key or `browser://<subject>/<relPath>`. */
  id: string;
  name: string;
  /** "Input" for the opened folder, else "job-N · DWI · registration" style. */
  group: string;
  isVolume: boolean;
  isMask: boolean;
  sizeBytes: number;
  /** Run number the file came from (0 for inputs), newest first in the list. */
  run: number;
}

const VOLUME = /\.nii(\.gz)?$/i;
const MASK = /(mask|stroke|HP[-_]|HP_manual|symClassic)/i;

export function stripExt(name: string): string {
  return name.replace(/\.nii(\.gz)?$/i, "").replace(/\.[a-z0-9]+$/i, "");
}

export function extOf(name: string): string {
  if (/\.nii\.gz$/i.test(name)) return "nii.gz";
  const m = name.match(/\.([a-z0-9]+)$/i);
  return m ? m[1].toLowerCase() : "";
}

function fromInput(subjectDir: string, f: DetectedFile): SimpleFile {
  return {
    id: `${subjectDir.replace(/\/+$/, "")}/${f.relPath}`,
    name: f.fileName,
    group: "Input",
    isVolume: f.fileType === "NIfTI",
    isMask: f.fileType === "NIfTI" && MASK.test(f.fileName),
    sizeBytes: f.sizeBytes,
    run: 0,
  };
}

interface SimpleState {
  inputs: SimpleFile[];
  outputs: SimpleFile[];
  baseId: string | null;
  overlayIds: string[];
  previewId: string | null;
  setInputs: (subjectDir: string, files: DetectedFile[]) => void;
  refreshOutputs: () => Promise<void>;
  setBase: (id: string | null) => void;
  toggleOverlay: (id: string) => void;
  setPreview: (id: string | null) => void;
  /** Click on a file anywhere: volumes go to the viewer, the rest to the preview. */
  show: (file: SimpleFile) => void;
  reset: () => void;
}

export const useSimple = create<SimpleState>((set, get) => ({
  inputs: [],
  outputs: [],
  baseId: null,
  overlayIds: [],
  previewId: null,

  setInputs: (subjectDir, files) => {
    const inputs = files.filter((f) => f.fileType !== "Other").map((f) => fromInput(subjectDir, f));
    const firstImage = inputs.find((f) => f.isVolume && !f.isMask && /dwi/i.test(f.name)) ?? inputs.find((f) => f.isVolume && !f.isMask);
    set({ inputs, baseId: firstImage?.id ?? null, overlayIds: [], previewId: null });
  },

  refreshOutputs: async () => {
    const { tree } = await listOutputs("current");
    const outputs = tree
      .filter((n) => !n.isDir)
      .map((n): SimpleFile => {
        const parts = n.relPath.split("/");
        const run = Number(parts[0]?.match(/^job-(\d+)$/)?.[1] ?? 0);
        const group = run ? `${parts[1]} · ${parts[2]}` : parts.slice(0, -1).join(" · ");
        return {
          id: n.relPath,
          name: n.name,
          group,
          isVolume: VOLUME.test(n.name),
          isMask: VOLUME.test(n.name) && MASK.test(n.name),
          sizeBytes: n.sizeBytes,
          run,
        };
      });
    set({ outputs });
  },

  setBase: (id) => set((s) => ({ baseId: id, overlayIds: s.overlayIds.filter((o) => o !== id) })),
  toggleOverlay: (id) =>
    set((s) => ({
      overlayIds: s.overlayIds.includes(id) ? s.overlayIds.filter((o) => o !== id) : [...s.overlayIds, id],
    })),
  setPreview: (id) => set({ previewId: id }),

  show: (file) => {
    if (!file.isVolume) {
      set({ previewId: file.id });
      return;
    }
    const { baseId } = get();
    if (file.isMask && baseId) get().toggleOverlay(file.id);
    else get().setBase(file.id);
  },

  reset: () => set({ inputs: [], outputs: [], baseId: null, overlayIds: [], previewId: null }),
}));

/** Every file either column can show, by id. */
export function useAllFiles(): SimpleFile[] {
  const inputs = useSimple((s) => s.inputs);
  const outputs = useSimple((s) => s.outputs);
  return React.useMemo(() => [...inputs, ...outputs], [inputs, outputs]);
}

export async function resolveUrl(id: string): Promise<string | null> {
  const { url } = await getAssetUrl(id);
  return url || null;
}
